/**
 * The signed out frame, in Harvey's look (day 33), as the approved sign in
 * prototype lays it out (Briefcase/Prototypes/hallquill-sign-in.html): a dark
 * panel with the wordmark and one statement, and the form beside it on the
 * paper. Below 900 pixels the panel goes and the wordmark stands over the
 * form, in one column.
 *
 * The night office film Mohit chose on 30 September 2026 plays behind the
 * panel, under a veil, on a wide screen only: on a phone there is no panel,
 * and neither the film nor its still is mounted or fetched. Day 34 decides whether it stays in this look.
 *
 * compact marks the longer steps after sign in, the profile and the plan,
 * which take a wider form. portal is the student portal; the admin site
 * passes neither, and gets one plain column with its own brand over the form.
 *
 * Everything a signed out browser sees is inside this frame. The app frame is
 * never mounted for it, so no navigation, name or route is in its DOM to be
 * glimpsed, scraped or read out by a screen reader.
 */

import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { BRAND_NAME, WORDMARK } from "../brand";
import { Wordmark } from "../ui";

/**
 * The film behind the panel (Mohit, 30 September 2026): a night office with
 * one person at a desk by a lamp (Pexels 4069295). 1280 wide, no sound,
 * fading from and to black so the loop does not jump.
 */
export const BACKDROP_VIDEO = "/media/signin-office-v2.mp4";
export const BACKDROP_STILL = "/media/signin-office-v2.jpg";

/** The width from which the panel, and so the film, is shown. */
export const PANEL_QUERY = "(min-width: 900px)";

interface NetworkInformation {
  readonly saveData?: boolean;
  readonly effectiveType?: string;
}

/**
 * Whether this visitor should get the still rather than the film: there is no
 * panel at this width, they asked for less motion or for less data, or the
 * connection is 2g. Not 3g: Chrome rates ordinary home connections 3g at
 * times, and the film is under a megabyte.
 */
export function stillOnly(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  if (!window.matchMedia(PANEL_QUERY).matches) return true;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true;
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  return Boolean(connection?.saveData) || /^(slow-2g|2g)$/.test(connection?.effectiveType ?? "");
}

/**
 * The still is in the page from the first paint. The film is fetched into
 * memory and played from there: Cloudflare Pages answers a Range request with
 * the whole file (200, never 206), and Safari refuses to play a video that
 * way. It fades in over the still once it is really playing, and if the fetch
 * fails the still simply stays.
 */
function Backdrop() {
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const element = video.current;
    if (!element || stillOnly()) return;
    let url: string | null = null;
    let live = true;
    void (async () => {
      try {
        const reply = await fetch(BACKDROP_VIDEO);
        if (!reply.ok || !live) return;
        url = URL.createObjectURL(await reply.blob());
        if (!live) return;
        element.muted = true;
        element.src = url;
        await element.play();
      } catch {
        // Fetch or autoplay refused, as a low power mode does: the still stays.
      }
    })();
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, []);
  return (
    <div class="auth-backdrop" aria-hidden="true">
      <img class="auth-still" src={BACKDROP_STILL} alt="" decoding="async" loading="lazy" />
      <video
        ref={video}
        class="auth-video"
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        tabIndex={-1}
        onPlaying={(event) => (event.currentTarget as HTMLVideoElement).classList.add("is-playing")}
      />
      <div class="auth-veil" />
    </div>
  );
}

/**
 * Whether the panel is showing. Its film and still are mounted only then, so a
 * phone, which has no panel, fetches neither (an image inside a hidden panel is
 * still fetched, lazy or not). Without a window, as in a unit test, the panel
 * is drawn.
 */
function usePanel(): boolean {
  const [wide, setWide] = useState(() => typeof window === "undefined" || typeof window.matchMedia !== "function" || window.matchMedia(PANEL_QUERY).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const list = window.matchMedia(PANEL_QUERY);
    const update = () => setWide(list.matches);
    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, []);
  return wide;
}

export function AuthLayout(props: { children: ComponentChildren; compact?: boolean; portal?: boolean; brand?: ComponentChildren }) {
  const panel = usePanel();
  if (!props.portal) {
    // The admin site's sign in (day 34): one plain column, its brand over the form.
    return (
      <div class="auth auth-plain">
        <main class="auth-form">
          <div class="auth-box">
            <div class="auth-brand">{props.brand ?? <Wordmark />}</div>
            {props.children}
          </div>
        </main>
      </div>
    );
  }
  return (
    <div class={props.compact ? "auth auth-portal auth-compact" : "auth auth-portal"}>
      <section class="auth-panel" aria-label={`${WORDMARK.lead}${WORDMARK.tail}`}>
        {panel ? <Backdrop /> : null}
        <Wordmark />
        <div class="auth-statement">
          <h2>
            The writing you submit, <em>exact</em> to the last citation.
          </h2>
          <p>Papers researched, refined and cited, and applications read by lawyers at top tier firms.</p>
        </div>
        {/* The business's real name, as the e-commerce rules ask; the year fixed at 2027 (Mohit, 29 September 2026). */}
        <p class="auth-foot">© 2027 {BRAND_NAME}</p>
      </section>
      <main class="auth-form">
        <div class="auth-box">
          <div class="auth-brand">
            <Wordmark />
          </div>
          {props.children}
        </div>
      </main>
    </div>
  );
}

/** Before the session is known: the paper and the wordmark, and no frame. */
export function Boot() {
  return (
    <div class="boot" aria-busy="true" aria-label="Loading">
      <Wordmark />
    </div>
  );
}
